<template>
    <div class="ex-tab">

        <div class="ej-tb">
            <div class="seg">
                <button class="si" :class="{ on: !isOwnerLibrary }" @click="isOwnerLibrary = false">BuilderPro</button>
                <button class="si" :class="{ on: isOwnerLibrary }" @click="isOwnerLibrary = true">{{ $t('common.own') }}</button>
            </div>
            <kimsa-text-field class="ej-search" @change="setSearch" :value="search" :placeholder="$t('common.search')"
                prepend-inner-icon="mdi-magnify" dense />
            <v-btn class="btn btn-ghost" depressed height="38" @click="showFilterExercises">
                <v-icon size="13" class="mr-1">mdi-filter-outline</v-icon>
                {{ $t('common.filter') }}
            </v-btn>
            <v-btn v-if="isOwnerLibrary" class="btn btn-secondary ml-auto" depressed height="38" @click="onEditAction()">
                <v-icon size="14" class="mr-1">$plus</v-icon>
                {{ $t('exercises.create_exercise') }}
            </v-btn>
        </div>

        <div class="cats">
            <button class="cc" :class="{ on: !activeCategoryId }" @click="onSelectCategoryChip(null)">
                {{ $t('common.all') }}
            </button>
            <button v-for="cat in categoriesOptions" :key="cat.id" class="cc"
                :class="{ on: activeCategoryId === cat.id }" @click="onSelectCategoryChip(cat)">
                {{ cat.label }}
            </button>
        </div>

        <div class="tw" @touchstart="$emit('touchless', true)" @touchend="$emit('touchless', false)"
            @touchcancel="$emit('touchless', false)">
            <div class="thead" :style="{ gridTemplateColumns: cols }">
                <div class="th"></div>
                <div class="th">{{ $t('common.name') }}</div>
                <div class="th">{{ $t('common.categories') }}</div>
                <div class="th">{{ $t('exercises.types') }}</div>
                <div class="th">{{ $t('exercises.tags') }}</div>
                <div class="th">{{ $t('equipments.equipments') }}</div>
                <template v-if="isOwnerLibrary">
                    <div class="th th-c">{{ $t('common.visible') }}</div>
                    <div class="th"></div>
                </template>
            </div>

            <skeleton-loader v-if="loading" type="rows" :count="8" class="ex-sk" />
            <div v-else-if="exercises.length === 0" class="tw-empty">{{ $t('common.no_data_yet') }}</div>
            <template v-else>
                <div v-for="item in exercises" :key="item.id" class="tr" :style="{ gridTemplateColumns: cols }">
                <div class="tmb" @click="onShowVideo(item)">
                    <img v-if="item.hasImg" :src="item.img" alt="">
                    <v-icon v-else size="14">mdi-image-outline</v-icon>
                </div>
                <div class="tn">{{ item.name }}</div>
                <div>
                    <span v-if="item.category" class="bdg" :style="categoryStyle(item.category)">
                        {{ item.category }}<template v-if="item.categoryExtra"> (+{{ item.categoryExtra }})</template>
                    </span>
                    <span v-else class="tnd">—</span>
                </div>
                <div :class="item.type === '—' ? 'tnd' : 'tt2'">{{ item.type }}</div>
                <div>
                    <span v-if="item.tag" class="tag-chip">
                        {{ item.tag }}<template v-if="item.tagExtra"> (+{{ item.tagExtra }})</template>
                    </span>
                    <span v-else class="tnd">—</span>
                </div>
                <div :class="item.equipments === '—' ? 'tnd' : 'tt2'">{{ item.equipments }}</div>
                <template v-if="isOwnerLibrary">
                    <div class="tc">
                        <v-progress-circular v-if="togglingId === item.id" indeterminate size="16" width="2"
                            color="accent" />
                        <template v-else>
                            <span v-if="!item.hide" class="von" @click="onHideAction(item.id)">
                                <v-icon size="13">mdi-eye-outline</v-icon>{{ $t('common.visible') }}
                            </span>
                            <span v-else class="voff" @click="onHideAction(item.id)">
                                <v-icon size="13">mdi-eye-off-outline</v-icon>{{ $t('common.hidden') }}
                            </span>
                        </template>
                    </div>
                    <div class="ra">
                        <button class="ibtn" @click="onEditAction(item.id)">
                            <v-icon size="13">$edit</v-icon>
                        </button>
                    </div>
                </template>
                </div>
            </template>
        </div>

        <div class="ex-pag" v-if="pageCount > 1">
            <kimsa-pagination @page="page = $event - 1" :pageCount="pageCount" :value="page + 1" />
        </div>

        <watch-video-dialog :thumb="currentImg" :video="currentVideo" ref="watchVideo" />
        <filter-excercises-dialog ref="filter" @filter="onFilterExercises" />
        <exercise-creator-desktop-dialog ref="exerciseCreator" :default-exercise="defaultExercise"
            @hide="defaultExercise = null" @success="onSuccessExercise" />
    </div>
</template>

<script>

import { db } from "@/utils/Firebase";
import ExerciseCreatorDesktopDialog from "@/components/coach/routines/ExerciseCreatorDesktopDialog";
import { ExerciseIndex } from "@/utils/Algolia";
import { invalidateExerciseCache } from "@/utils/ExerciseLibrary";
import { debounce } from "@/utils/Common";
import KimsaPagination from '../../common/KimsaPagination.vue';
import KimsaTextField from '../../common/inputs/KimsaTextField.vue';
import WatchVideoDialog from '../../common/routines/WatchVideoDialog.vue';
import FilterExcercisesDialog from '../../common/FilterExcercisesDialog.vue';
import SkeletonLoader from '../../common/SkeletonLoader.vue';
import { mapState, mapActions } from "vuex";

const CATEGORY_PALETTE = [
    { background: 'rgba(217,119,6,.15)', color: '#FCD34D' },
    { background: 'rgba(139,92,246,.15)', color: '#C4B5FD' },
    { background: 'rgba(234,179,8,.15)', color: '#FDE68A' },
    { background: 'rgba(16,185,129,.15)', color: '#6EE7B7' },
    { background: 'rgba(59,130,246,.15)', color: '#93C5FD' },
    { background: 'rgba(51,153,153,.15)', color: '#66CCCC' },
]

export default {
    name: "ExercisesTab",
    components: { ExerciseCreatorDesktopDialog, KimsaPagination, KimsaTextField, WatchVideoDialog, FilterExcercisesDialog, SkeletonLoader },
    data() {
        return {
            search: '',
            loading: false,
            exercisesDefaults: [],
            exercises: [],
            defaultExercise: null,
            page: 0,
            pageCount: 0,
            perPage: 10,
            currentImg: null,
            currentVideo: null,
            isOwnerLibrary: true,
            filterCategories: null,
            togglingId: null,
        }
    },
    computed: {
        ...mapState(['libraryCoach', 'categoriesOptions']),
        cols(vm = this) {
            return vm.isOwnerLibrary
                ? '44px minmax(160px, 1fr) 120px 120px 90px 140px 90px 46px'
                : '44px minmax(160px, 1fr) 120px 120px 90px 140px'
        },
        activeCategoryId(vm = this) {
            return vm.filterCategories?.category?.id || null
        },
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser?.uid || null
        },
        coachId(vm = this) {
            if (vm.isCompany) return vm.libraryCoach?.id || null
            return vm.currentUser?.uid || null
        },
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
        await vm.loadExercises()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        async loadExercises() {
            const vm = this
            if (!vm.account) return
            vm.loading = true

            let filterCompany = ''
            if (vm.account.accountType === 'coach_company' || vm.isCompany) {
                filterCompany = ` OR companyId: ${vm.companyId}`
            }
            let filter = `coachId: ${vm.coachId} ${filterCompany}`

            if (!vm.isOwnerLibrary) {
                filter = 'owner:app AND hide != 1'
            }

            if (vm.filterCategories) {
                if (vm.filterCategories.category) {
                    filter += ` AND categories: "${vm.filterCategories.category.name}"`
                }
                if (vm.filterCategories.types && vm.filterCategories.types.length > 0) {
                    let typesFilter = vm.filterCategories.types.map(type => `types: "${type.name}"`).join(' OR ')
                    filter += ` AND (${typesFilter})`
                }
            }

            let exercisesResponse = await ExerciseIndex.search(vm.search.toUpperCase(), {
                filters: filter,
                hitsPerPage: vm.perPage,
                page: vm.page,
            })

            vm.pageCount = exercisesResponse.nbPages || 0

            vm.exercisesDefaults = exercisesResponse.hits

            vm.exercises = exercisesResponse.hits.map(exercise => vm.mapExercise(exercise))

            vm.loading = false
        },
        mapExercise(exercise) {
            const vm = this
            const hasImg = !!(exercise.youtubeUrl || exercise.img || exercise.video?.thumb)
            const categories = exercise.categories || []
            const tags = exercise.tags || []
            return {
                id: exercise.objectID,
                objectID: exercise.objectID,
                hasImg,
                img: exercise.youtubeUrl ? vm.getYoutubeImgUrl(exercise.youtubeUrl) : (exercise.img || exercise.video?.thumb || null),
                name: exercise.name,
                category: categories[0] || null,
                categoryExtra: Math.max(categories.length - 1, 0),
                type: vm.getArrayString(exercise.types),
                tag: tags[0] || null,
                tagExtra: Math.max(tags.length - 1, 0),
                equipments: vm.getArrayString(exercise.equipments),
                hide: exercise.hide || false,
                video: exercise.youtubeUrl ? null : exercise.video || null,
                youtubeUrl: exercise.youtubeUrl || null,
            }
        },
        onSuccessExercise(exercise) {
            const vm = this
            if (!exercise?.objectID) return
            const index = vm.exercisesDefaults.findIndex(ex => (ex?.id || ex?.objectID) === exercise.objectID)
            if (index === -1) {
                vm.exercisesDefaults.unshift(exercise)
                vm.exercises.unshift(vm.mapExercise(exercise))
            } else {
                const merged = { ...vm.exercisesDefaults[index], ...exercise }
                vm.$set(vm.exercisesDefaults, index, merged)
                vm.$set(vm.exercises, index, vm.mapExercise(merged))
            }
        },
        getArrayString(array = []) {
            if (!array || array.length === 0) return '—'
            if (array.length <= 1) return array.join(', ')
            return `${array[0]} (+${array.length - 1})`
        },
        categoryStyle(name) {
            let hash = 0
            for (let i = 0; i < (name || '').length; i++) hash = (hash + name.charCodeAt(i)) % CATEGORY_PALETTE.length
            return CATEGORY_PALETTE[hash]
        },
        async onHideAction(exerciseId) {
            const vm = this
            if (vm.togglingId) return
            vm.togglingId = exerciseId
            try {
                let exercise = vm.exercises.find(exercise => exercise.id === exerciseId)
                let hide = !exercise?.hide
                await db.collection('exercises').doc(exerciseId).update({ hide })
                invalidateExerciseCache()
                let exerciseIndex = vm.exercises.findIndex(exercise => exercise.id === exerciseId)
                if (exerciseIndex !== -1) vm.exercises[exerciseIndex].hide = hide
                vm.toast(hide ? vm.$t('common.hidden_exercise') : vm.$t('common.visible_exercise'))
            } finally {
                vm.togglingId = null
            }
        },
        onEditAction(exerciseId) {
            const vm = this
            if (exerciseId) vm.defaultExercise = vm.exercisesDefaults.find(exercise => (exercise?.id || exercise?.objectID) === exerciseId)
            vm.$refs.exerciseCreator.show()
        },
        setSearch(search) {
            const vm = this;
            vm.loading = true
            vm.search = search || ''
            vm.page = 0
            vm.debouncedLoadExercises()
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 1000),
        refresh() {
            const vm = this;
            vm.loadExercises()
        },
        async onShowVideo(item) {
            const vm = this
            vm.currentImg = item.img || item.video?.thumb || null
            const isYoutubeVideo = item?.youtubeUrl ? true : false
            vm.currentVideo = isYoutubeVideo ? item.youtubeUrl : item.video?.url || null
            if (vm.currentVideo) vm.$refs.watchVideo.show(isYoutubeVideo)
        },
        showFilterExercises() {
            const vm = this;
            vm.$refs.filter.show(vm.filterCategories)
        },
        onSelectCategoryChip(category) {
            const vm = this
            if (!category) vm.filterCategories = null
            else vm.filterCategories = { category, types: [] }
            vm.page = 0
            vm.loadExercises()
        },
        onFilterExercises(filter = null) {
            const vm = this;
            vm.filterCategories = filter
            vm.page = 0
            vm.loadExercises()
        }
    },
    watch: {
        page(val, oldVal) {
            if (val !== oldVal) this.loadExercises()
        },
        isOwnerLibrary(val, oldVal) {
            if (val !== oldVal) {
                this.page = 0
                this.loadExercises()
            }
        },
    }
}
</script>

<style scoped>
.ej-tb {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.seg {
    display: flex;
    border: 1px solid var(--v-outline2-base);
    border-radius: 8px;
    overflow: hidden;
}

.si {
    padding: 7px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    border: none;
    user-select: none;
    transition: all .15s;
}

.theme--dark .si.on {
    background: var(--v-primary-base);
    color: #0C0E12;
}

.theme--light .si.on {
    background: var(--v-accent-base);
    color: #fff;
}

.si:first-child {
    border-right: 1px solid var(--v-outline2-base);
}

.ej-search {
    max-width: 260px;
    flex: 1 1 200px;
}

.btn {
    text-transform: none !important;
    letter-spacing: normal !important;
    font-weight: 700;
    border-radius: 8px !important;
    min-width: auto !important;
    padding: 0 16px !important;
}

.btn-ghost {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    border: 1px solid var(--v-outline-base) !important;
}

.btn-ghost:hover {
    color: var(--v-txtPrimary-base) !important;
    border-color: var(--v-outline2-base) !important;
}

.btn-secondary {
    background: linear-gradient(135deg, rgba(204, 255, 51, .15), rgba(51, 153, 153, .15)) !important;
    color: var(--v-primary-base) !important;
    border: 1.5px solid rgba(204, 255, 51, .5) !important;
}

.theme--light .btn-secondary {
    color: var(--v-accent-base) !important;
    border-color: rgba(51, 153, 153, .4) !important;
}

.cats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.cc {
    height: 26px;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtMuted-base);
    transition: all .15s;
    display: inline-flex;
    align-items: center;
}

.cc:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.cc.on {
    background: linear-gradient(135deg, rgba(204, 255, 51, .15), rgba(51, 153, 153, .15));
    border-color: rgba(204, 255, 51, .5);
    color: var(--v-primary-base);
}

.theme--light .cc.on {
    color: var(--v-accent-base);
    border-color: rgba(51, 153, 153, .4);
}

.tw {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.thead {
    display: grid;
    height: 36px;
    align-items: center;
    padding: 0 16px;
    gap: 10px;
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.th {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .06em;
    white-space: nowrap;
}

.th-c {
    text-align: center;
}

.tr {
    display: grid;
    min-height: 56px;
    align-items: center;
    padding: 0 16px;
    gap: 10px;
    border-bottom: 1px solid var(--v-outline-base);
    transition: background .12s;
}

.tr:last-of-type {
    border-bottom: none;
}

.tr:hover {
    background: var(--v-surface2-base);
}

.tr:hover .ra {
    opacity: 1;
}

.tmb {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    overflow: hidden;
    background: var(--v-surface2-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
}

.tmb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.tmb .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.tn {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tt2 {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tnd {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.tc {
    display: flex;
    justify-content: center;
    align-items: center;
}

.bdg {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    background: rgba(51, 153, 153, .15);
    border: 1px solid var(--v-accent-base);
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    color: var(--v-secondary-base);
    white-space: nowrap;
}

.von {
    color: var(--v-success-base);
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    padding: 3px 7px;
    border-radius: 4px;
    transition: background .15s;
}

.voff {
    color: var(--v-txtMuted-base);
    font-size: 11px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    padding: 3px 7px;
    border-radius: 4px;
    transition: background .15s;
}

.von:hover {
    background: rgba(0, 153, 102, .1);
}

.voff:hover {
    background: var(--v-surface2-base);
}

.von .v-icon,
.voff .v-icon {
    color: inherit !important;
}

.ra {
    display: flex;
    align-items: center;
    gap: 3px;
    opacity: 0;
    transition: opacity .15s;
}

.ibtn {
    width: 32px;
    height: 32px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    transition: all .15s;
    flex-shrink: 0;
}

.ibtn:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.ibtn .v-icon {
    color: inherit !important;
}

.tw-empty {
    padding: 32px;
    text-align: center;
    color: var(--v-txtMuted-base);
    font-size: 13px;
}

.ex-sk {
    padding: 4px 14px;
}

.ex-pag {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
}

@media (max-width: 960px) {
    .tw {
        overflow-x: auto;
    }

    .thead,
    .tr {
        min-width: 720px;
    }

    .ra {
        opacity: 1;
    }
}
</style>
